import React, { useState } from 'react';
import './Help.css';
import { getEnvironmentConfig } from '../config/environment';

const Help = () => {
  const [feedbackForm, setFeedbackForm] = useState({
    name: '',
    email: '',
    type: 'Feedback',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const socialMediaLinks = [
    {
      name: 'Instagram',
      handle: '@fideantech',
      url: 'https://instagram.com/fideantech',
      icon: '📸',
      color: '#E4405F'
    },
    {
      name: 'YouTube',
      handle: '@fiddyconnect',
      url: 'https://youtube.com/@fiddyconnect',
      icon: '📺',
      color: '#FF0000'
    },
    {
      name: 'X (Twitter)',
      handle: '@fiddyscript',
      url: 'https://x.com/fiddyscript',
      icon: '🐦',
      color: '#000000'
    },
    {
      name: 'Telegram',
      handle: 't.me/fiddyconnect',
      url: 'https://t.me/fiddyconnect',
      icon: '📱',
      color: '#0088CC'
    },
    {
      name: 'Support',
      handle: 'fideantech.com/support',
      url: 'https://fideantech.com/support',
      icon: '🆘',
      color: '#5A3EC8'
    }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!feedbackForm.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (feedbackForm.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!feedbackForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(feedbackForm.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Type validation
    if (!feedbackForm.type) {
      newErrors.type = 'Please select a feedback type';
    }

    // Message validation
    if (!feedbackForm.message.trim()) {
      newErrors.message = 'Feedback message is required';
    } else if (feedbackForm.message.trim().length < 10) {
      newErrors.message = 'Feedback must be at least 10 characters';
    } else if (feedbackForm.message.trim().length > 750) {
      newErrors.message = 'Feedback must be 750 words or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Get the correct API URL from environment config
      const config = getEnvironmentConfig();
      const apiUrl = config.apiUrls[0]; // Use the first API URL
      
      console.log('Submitting feedback to:', `${apiUrl}/api/feedback`);
      
      // Send feedback to the API server
      const response = await fetch(`${apiUrl}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: feedbackForm.name.trim(),
          email: feedbackForm.email.trim(),
          type: feedbackForm.type,
          message: feedbackForm.message.trim()
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const savedFeedback = await response.json();
      console.log('Feedback submitted successfully:', savedFeedback);
      
      // Show success notification
      setSubmitStatus('success');
      
      // Clear form
      setFeedbackForm({
        name: '',
        email: '',
        type: 'Feedback',
        message: ''
      });
      
      // Show success message for longer
      setTimeout(() => setSubmitStatus(null), 8000);
      
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
      
      // Show error message for longer
      setTimeout(() => setSubmitStatus(null), 8000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSocialLinkClick = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="help-container">
      <div className="help-header">
        <h1>Help & Support</h1>
        <p>Get help, connect with us, and share your feedback</p>
      </div>

      <div className="help-content">
        {/* Social Media Section */}
        <div className="help-section">
          <h2>Connect With Us</h2>
          <p>Follow us on social media for updates, tips, and community support:</p>
          
          <div className="social-media-grid">
            {socialMediaLinks.map((social, index) => (
              <div
                key={index}
                className="social-media-card"
                onClick={() => handleSocialLinkClick(social.url)}
                style={{ borderColor: social.color }}
              >
                <div className="social-icon" style={{ color: social.color }}>
                  {social.icon}
                </div>
                <div className="social-info">
                  <h3>{social.name}</h3>
                  <p>{social.handle}</p>
                </div>
                <div className="social-arrow">→</div>
              </div>
            ))}
          </div>
        </div>

        {/* Feedback Form Section */}
        <div className="help-section">
          <h2>Send Us Feedback/Testimonial/Suggestions</h2>
          <p>We'd love to hear from you! Share your suggestions, report issues, or tell us about your experience.</p>
          
          {/* Status Notification - Moved to top for visibility */}
          {submitStatus && (
            <div className={`submit-status ${submitStatus}`}>
              {submitStatus === 'success' ? (
                <>
                  <span className="status-icon">🎉</span>
                  <span>
                    <strong>Success!</strong> Your feedback has been submitted successfully. 
                    We'll review it and get back to you soon. Thank you for your input!
                  </span>
                </>
              ) : (
                <>
                  <span className="status-icon">⚠️</span>
                  <span>
                    <strong>Oops!</strong> Failed to send feedback. Please check your connection and try again.
                  </span>
                </>
              )}
            </div>
          )}
          
          <form className="feedback-form" onSubmit={handleSubmit}>
            <div className="feedback-form-content">
                             <div className="form-group">
                 <label htmlFor="name">Name</label>
                 <input
                   type="text"
                   id="name"
                   value={feedbackForm.name}
                   onChange={(e) => handleInputChange('name', e.target.value)}
                   className={errors.name ? 'error' : ''}
                   placeholder="Enter your name"
                 />
                 {errors.name && <span className="error-message">{errors.name}</span>}
               </div>

                               <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input
                    type="email"
                    id="email"
                    value={feedbackForm.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    className={errors.email ? 'error' : ''}
                    placeholder="Enter your email"
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label>Feedback Type</label>
                  <div className="radio-group">
                    {['Bug', 'Feedback', 'Suggestion', 'Testimonial'].map((type) => (
                      <label key={type} className="radio-option">
                        <input
                          type="radio"
                          name="type"
                          value={type}
                          checked={feedbackForm.type === type}
                          onChange={(e) => handleInputChange('type', e.target.value)}
                        />
                        <span className="radio-label">{type}</span>
                      </label>
                    ))}
                  </div>
                  {errors.type && <span className="error-message">{errors.type}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    value={feedbackForm.message}
                    onChange={(e) => handleInputChange('message', e.target.value)}
                    className={errors.message ? 'error' : ''}
                    placeholder="Share your feedback, testimonial, or suggestions..."
                    rows="4"
                    maxLength="750"
                  />
                  <div className="textarea-footer">
                    <span className="char-count">
                      {feedbackForm.message.length}/750 characters
                    </span>
                    {errors.message && <span className="error-message">{errors.message}</span>}
                  </div>
                </div>

              <div className="form-actions">
                <button
                  type="submit"
                  className="submit-btn"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </div>
          </form>
        </div>


      </div>
    </div>
  );
};

export default Help;
